import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import clsx from 'clsx';
import {Loader2, Plus, Save, Upload, X} from 'lucide-react';
import AdminService from '@/modules/admin/services/adminService';
import Button from '@/shared/components/Button';
import {useAdminNotifications} from '@/modules/admin/contexts/AdminNotificationContext';
import {logError} from '@/shared/utils/logger';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import {BillingType, BioMatch, BioMatchType, BiomarkerRule, RecommendationType, RiskTier} from '../types';
import {
    createEmptyRule,
    formatRecommendationTypeLabel,
    formatRiskTierLabel,
    groupRulesByIndex,
    isFieldVisible,
    requiresRangeInputs,
} from '../utils';
import {useBiomarkerSearch} from '../hooks/useBiomarkerSearch';
import {BiomarkerRuleRow} from './BiomarkerRuleRow';
import RecommendedListPicker, {type PickerOption} from '@/shared/components/RecommendedListPicker';
import {useQuery} from '@tanstack/react-query';

interface Props {
    open: boolean;
    editingId: number | null;
    initialValue: BioMatch | null;
    orgId?: number;
    /** Super-admin global authoring path. Routes saves to scope=global. */
    globalScope?: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const emptyState: BioMatch = {
    id: 0,
    bm_type: BioMatchType.CURATED,
    name: '',
    enabled: false,
    content: '',
    image: '',
    sup_type: '',
    price: 0,
    members_price: 0,
    url: '',
    recommendation_type: RecommendationType.SUPPLEMENT,
    risk_tier: null,
    priority_weight: null,
    cta_button_label: '',
    cta_body_text: '',
    kit_sku: '',
    pathway_url: '',
    track_enrollment: false,
    billing_type: BillingType.EMPLOYER_FULL,
    employer_coverage_percent: null,
    biomarkers: [createEmptyRule(0)],
    recommended_assays: [],
    recommended_products: [],
};

export const BioMatchModal: React.FC<Props> = ({
    open,
    editingId,
    initialValue,
    orgId,
    globalScope,
    onClose,
    onSaved,
}) => {
    const {showSuccess, showError} = useAdminNotifications();
    const [state, setState] = useState<BioMatch>(emptyState);
    const [submitting, setSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageMode, setImageMode] = useState<'upload' | 'url'>('upload');
    // If a create succeeds but a subsequent step (image upload) fails, remember
    // the new record's id so a retry becomes an update — not another create.
    const [createdId, setCreatedId] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const search = useBiomarkerSearch();

    const effectiveId = editingId ?? createdId;

    useEffect(() => {
        if (open) {
            const next = initialValue ?? {...emptyState, biomarkers: [createEmptyRule(0)]};
            setState(next);
            setImageFile(null);
            setImagePreview(null);
            const isUploadedImage = next.image?.startsWith('/api/public-media');
            setImageMode(next.image && !isUploadedImage ? 'url' : 'upload');
            setCreatedId(null);
        }
    }, [open, initialValue]);

    const updateRule = (flatIndex: number, patch: Partial<BiomarkerRule>) => {
        setState((s) => ({
            ...s,
            biomarkers: s.biomarkers.map((rule, i) => (i === flatIndex ? {...rule, ...patch} : rule)),
        }));
    };

    const removeRule = (flatIndex: number) => {
        setState((s) => {
            const next = s.biomarkers.filter((_, i) => i !== flatIndex);
            // If removing leaves us with zero rules, keep one empty rule
            // so the form is always valid to look at and the engine has
            // something to evaluate.
            return {
                ...s,
                biomarkers: next.length === 0 ? [createEmptyRule(0)] : normalizeGroupIndices(next),
            };
        });
    };

    /**
     * After a remove, group indices may have a gap (e.g. [0, 2] if
     * group 1's only rule was removed). Renumber to be contiguous so
     * the BE doesn't have to deal with sparse indices.
     */
    const normalizeGroupIndices = (rules: BiomarkerRule[]): BiomarkerRule[] => {
        const seen = new Set<number>();
        rules.forEach((r) => seen.add(r.group_index ?? 0));
        const sorted = Array.from(seen).sort((a, b) => a - b);
        const remap = new Map<number, number>();
        sorted.forEach((idx, position) => remap.set(idx, position));
        return rules.map((r) => ({...r, group_index: remap.get(r.group_index ?? 0) ?? 0}));
    };

    /** "Add another rule" — append to the last group (preserves AND grouping). */
    const addRuleToLastGroup = () => {
        setState((s) => {
            const lastGroupIndex = s.biomarkers.reduce((max, r) => Math.max(max, r.group_index ?? 0), 0);
            return {...s, biomarkers: [...s.biomarkers, createEmptyRule(lastGroupIndex)]};
        });
    };

    /** "Add OR group" — start a new group_index with one empty rule. */
    const addOrGroup = () => {
        setState((s) => {
            const nextGroupIndex = s.biomarkers.reduce((max, r) => Math.max(max, r.group_index ?? 0), 0) + 1;
            return {...s, biomarkers: [...s.biomarkers, createEmptyRule(nextGroupIndex)]};
        });
    };

    const hasImage = imageMode === 'upload' ? !!imageFile || !!state.image : !!state.image.trim();
    const recType = state.recommendation_type;
    // In range mode (super-admin global library), the rec is a
    // diagnostic rule — no marketing/pricing/CTA fields. Hide those
    // sections and skip their validation; show only Name + Rules +
    // Recommended Assays + Recommended Products + Enabled.
    const isRange = !!globalScope;

    // ── Recommended Assays + Recommended Products data ──────────────
    // Assay catalog drives the "what to retest" multi-select for range
    // recs. Storefront products drive the supplements/programs picker
    // for both rec types. Both queries cache across modal opens.
    const assayCatalogQuery = useQuery({
        queryKey: ['admin', 'assay-catalog'],
        queryFn: () => AdminService.getAssayCatalog(),
        enabled: open,
        staleTime: 5 * 60 * 1000,
    });
    const productsQuery = useQuery({
        queryKey: ['admin', 'storefront-products', globalScope ? 'global' : (orgId ?? 'none')],
        queryFn: () =>
            globalScope
                ? AdminService.listGlobalStorefrontProducts()
                : orgId
                  ? AdminService.listStorefrontProducts(orgId)
                  : Promise.resolve([]),
        enabled: open && (globalScope || !!orgId),
        staleTime: 5 * 60 * 1000,
    });

    const assayOptions: PickerOption[] = useMemo(() => {
        const items = assayCatalogQuery.data ?? [];
        // Plain assays first (alphabetical), then calculated assays
        // (alphabetical). Mirrors the LabTestPricing sort the user asked
        // for — admins read the catalog the same way everywhere.
        return items
            .slice()
            .sort((a, b) => {
                const calcDelta = Number(a.is_calculated) - Number(b.is_calculated);
                if (calcDelta !== 0) return calcDelta;
                return a.name.localeCompare(b.name);
            })
            .map((a) => ({
                key: a.code,
                label: a.name,
                sublabel: a.is_calculated ? 'calculated' : undefined,
            }));
    }, [assayCatalogQuery.data]);

    const productOptions: PickerOption[] = useMemo(() => {
        const items = productsQuery.data ?? [];
        return items
            .filter((p) => p.is_active)
            .map((p) => ({
                key: String(p.id),
                label: p.name,
                sublabel: `$${(p.price_cents / 100).toFixed(2)}`,
            }));
    }, [productsQuery.data]);

    const selectedAssayKeys = state.recommended_assays.map((a) => a.assay_code);
    const selectedProductKeys = state.recommended_products.map((p) => String(p.storefront_product_id));

    const setRecommendedAssayKeys = (keys: string[]) =>
        setState((s) => ({
            ...s,
            recommended_assays: keys.map((key, idx) => ({assay_code: key, sort_order: idx})),
        }));

    const setRecommendedProductKeys = (keys: string[]) =>
        setState((s) => ({
            ...s,
            recommended_products: keys
                .map((key, idx) => ({
                    storefront_product_id: parseInt(key, 10),
                    sort_order: idx,
                }))
                .filter((p) => Number.isFinite(p.storefront_product_id)),
        }));

    const isFormValid = (): boolean => {
        if (!state.name.trim()) return false;
        // Marketing/pricing fields are curated-only.
        if (!isRange) {
            if (!hasImage) return false;
            if (!state.cta_button_label.trim()) return false;
            if (isFieldVisible('price', recType) && state.price <= 0) return false;
            if (isFieldVisible('members_price', recType) && state.members_price <= 0) return false;
            if (isFieldVisible('kit_sku', recType) && !state.kit_sku.trim()) return false;
            if (isFieldVisible('pathway_url', recType) && !state.pathway_url.trim()) return false;
        }
        // Range recs may save with zero assays: a product-only rec is a
        // valid shape (the engine surfaces its products alongside the
        // Custom Lab Package — assembleBioMatchOutput pools products from
        // every matched range rec, and the package builds whenever it has
        // assays OR products).
        for (const rule of state.biomarkers) {
            if (!rule.test_type.trim()) return false;
            if (requiresRangeInputs(rule.rule_type)) {
                if (rule.min_value === null || rule.min_value === undefined) return false;
                if (rule.max_value === null || rule.max_value === undefined) return false;
            } else if (rule.threshold_value === null || rule.threshold_value === undefined) {
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (submitting) return;

        for (const b of state.biomarkers) {
            if (requiresRangeInputs(b.rule_type)) {
                const minVal = typeof b.min_value === 'number' ? b.min_value : Number.NEGATIVE_INFINITY;
                const maxVal = typeof b.max_value === 'number' ? b.max_value : Number.POSITIVE_INFINITY;
                if (!(minVal < maxVal)) {
                    showError(`For ${b.test_type}, minimum must be less than maximum`);
                    return;
                }
            }
        }

        const normalizedRules = normalizeGroupIndices(state.biomarkers);

        const body = {
            name: state.name,
            enabled: state.enabled,
            content: state.content,
            image: imageMode === 'url' ? state.image : state.image || null,
            sup_type: state.sup_type || null,
            // Price and members_price are still sent on the wire but the
            // BE only persists them for types where they're meaningful;
            // the visibility matrix above also prevents the admin from
            // typing values that won't be used.
            price: isFieldVisible('price', recType) ? state.price : null,
            members_price: isFieldVisible('members_price', recType) ? state.members_price : null,
            url: state.url || null,
            recommendation_type: state.recommendation_type,
            risk_tier: state.risk_tier,
            priority_weight: state.priority_weight,
            cta_button_label: state.cta_button_label.trim(),
            cta_body_text: state.cta_body_text.trim() || null,
            kit_sku: isFieldVisible('kit_sku', recType) ? state.kit_sku.trim() || null : null,
            pathway_url: isFieldVisible('pathway_url', recType) ? state.pathway_url.trim() || null : null,
            track_enrollment: isFieldVisible('track_enrollment', recType) ? state.track_enrollment : false,
            billing_type: state.billing_type,
            employer_coverage_percent:
                state.billing_type === BillingType.EMPLOYER_PARTIAL ? state.employer_coverage_percent : null,
            biomarkers: normalizedRules.map((b, i) => ({
                test_type: b.test_type.trim(),
                assay_code: b.assay_code ?? null,
                rule_type: b.rule_type,
                group_index: b.group_index ?? 0,
                sort_order: i,
                rule_source: b.rule_source,
                measurement_key: b.measurement_key ?? null,
                min_value: requiresRangeInputs(b.rule_type) ? (b.min_value ?? null) : null,
                max_value: requiresRangeInputs(b.rule_type) ? (b.max_value ?? null) : null,
                threshold_value: !requiresRangeInputs(b.rule_type) ? (b.threshold_value ?? null) : null,
                rule_expression: b.rule_expression ?? '',
                gender: b.gender ?? null,
            })),
            recommended_assays: state.recommended_assays.map((a, i) => ({
                assay_code: a.assay_code,
                sort_order: a.sort_order ?? i,
            })),
            recommended_products: state.recommended_products.map((p, i) => ({
                storefront_product_id: p.storefront_product_id,
                sort_order: p.sort_order ?? i,
            })),
        };

        setSubmitting(true);
        let recordId: number | null = effectiveId;
        try {
            if (effectiveId) {
                await AdminService.updateRecommendation(effectiveId, body, orgId, {globalScope});
            } else {
                const created = await AdminService.addRecommendation(body, orgId, {globalScope});
                recordId = typeof created?.id === 'number' ? created.id : null;
                if (recordId != null) setCreatedId(recordId);
            }
        } catch (error) {
            logError('Failed to save BioMatch', error);
            showError(effectiveId ? 'Could not update BioMatch' : 'Could not save BioMatch');
            setSubmitting(false);
            return;
        }

        onSaved();

        if (imageMode === 'upload' && imageFile && recordId != null) {
            try {
                await AdminService.uploadRecommendationImage(recordId, imageFile, orgId, {globalScope});
            } catch (error) {
                logError('Failed to upload BioMatch image', error);
                showError('BioMatch saved, but image upload failed');
                setSubmitting(false);
                onSaved();
                return;
            }
            onSaved();
        }

        showSuccess(effectiveId ? 'BioMatch updated' : 'BioMatch created');
        setSubmitting(false);
        onClose();
    };

    const groups = groupRulesByIndex(state.biomarkers);

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="max-w-4xl rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                <form onSubmit={handleSubmit}>
                                    <div className="flex items-start justify-between border-b border-mld-panel-border px-6 py-4">
                                        <div>
                                            <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                                {editingId ? 'Edit BioMatch' : 'Build New BioMatch'}
                                            </Dialog.Title>
                                            <p className="text-xs text-gray-500">
                                                Configure a recommendation. Within a rule group all rules must match
                                                (AND); separate groups are evaluated independently (OR).
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            aria-label="Close"
                                            className="text-gray-400 hover:text-gray-600">
                                            <X className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <div className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
                                        {/* Name */}
                                        <div>
                                            <label
                                                htmlFor="biomatch-name"
                                                className="mb-1 block text-xs font-medium text-gray-700">
                                                Name <span className="text-mld-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="biomatch-name"
                                                value={state.name}
                                                onChange={(e) => setState({...state, name: e.target.value})}
                                                maxLength={50}
                                                placeholder="Enter a name (max 50 characters)"
                                                required
                                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </div>

                                        {/* Description */}
                                        <div>
                                            <label
                                                htmlFor="biomatch-content"
                                                className="mb-1 block text-xs font-medium text-gray-700">
                                                Description{' '}
                                                <span className="font-normal text-gray-400">(optional)</span>
                                            </label>
                                            <textarea
                                                id="biomatch-content"
                                                value={state.content || ''}
                                                onChange={(e) => setState({...state, content: e.target.value})}
                                                maxLength={200}
                                                placeholder="Enter a short description (max 200 characters)"
                                                rows={3}
                                                className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </div>

                                        {/* ── Marketing / Pricing — curated only ── */}
                                        {!isRange && (
                                            <>
                                                {/* Image */}
                                                <div>
                                                    <div className="mb-2 flex items-center gap-2">
                                                        <label className="text-xs font-medium text-gray-700">
                                                            Image <span className="text-mld-danger">*</span>
                                                        </label>
                                                        <div className="flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-[10px]">
                                                            <button
                                                                type="button"
                                                                onClick={() => setImageMode('upload')}
                                                                className={clsx(
                                                                    'rounded px-2 py-0.5 font-medium transition',
                                                                    imageMode === 'upload'
                                                                        ? 'bg-white text-mld-primary shadow-sm'
                                                                        : 'text-gray-500'
                                                                )}>
                                                                Upload
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setImageMode('url')}
                                                                className={clsx(
                                                                    'rounded px-2 py-0.5 font-medium transition',
                                                                    imageMode === 'url'
                                                                        ? 'bg-white text-mld-primary shadow-sm'
                                                                        : 'text-gray-500'
                                                                )}>
                                                                URL
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {imageMode === 'upload' ? (
                                                        <div>
                                                            <button
                                                                type="button"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-4 text-sm text-gray-500 hover:border-mld-primary hover:text-mld-primary">
                                                                <Upload className="h-4 w-4" />
                                                                {imageFile
                                                                    ? imageFile.name
                                                                    : state.image
                                                                      ? 'Replace image...'
                                                                      : 'Choose image...'}
                                                            </button>
                                                            <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                accept="image/png,image/jpeg,image/webp,image/gif"
                                                                className="hidden"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) {
                                                                        setImageFile(file);
                                                                        setImagePreview(URL.createObjectURL(file));
                                                                    }
                                                                }}
                                                            />
                                                            {(imagePreview || state.image) && (
                                                                <img
                                                                    src={imagePreview || resolveMediaUrl(state.image)}
                                                                    alt="Preview"
                                                                    className="mt-2 h-20 rounded-md border border-gray-200 object-contain"
                                                                />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-start gap-3">
                                                            {state.image && (
                                                                <img
                                                                    src={resolveMediaUrl(state.image)}
                                                                    alt="Current"
                                                                    className="h-12 w-12 flex-shrink-0 rounded-md border border-gray-200 object-contain"
                                                                    onError={(e) => {
                                                                        (
                                                                            e.currentTarget as HTMLImageElement
                                                                        ).style.display = 'none';
                                                                    }}
                                                                />
                                                            )}
                                                            <input
                                                                type="url"
                                                                value={state.image}
                                                                onChange={(e) =>
                                                                    setState({...state, image: e.target.value})
                                                                }
                                                                placeholder="https://..."
                                                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                            />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Recommendation Type + Risk Tier + Priority */}
                                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-rec-type"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Recommendation Type{' '}
                                                            <span className="text-mld-danger">*</span>
                                                        </label>
                                                        <select
                                                            id="biomatch-rec-type"
                                                            value={state.recommendation_type}
                                                            onChange={(e) =>
                                                                setState({
                                                                    ...state,
                                                                    recommendation_type: e.target
                                                                        .value as RecommendationType,
                                                                })
                                                            }
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                                                            {Object.values(RecommendationType).map((t) => (
                                                                <option key={t} value={t}>
                                                                    {formatRecommendationTypeLabel(t)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-risk-tier"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Risk Tier
                                                        </label>
                                                        <select
                                                            id="biomatch-risk-tier"
                                                            value={state.risk_tier ?? ''}
                                                            onChange={(e) =>
                                                                setState({
                                                                    ...state,
                                                                    risk_tier: e.target.value
                                                                        ? (e.target.value as RiskTier)
                                                                        : null,
                                                                })
                                                            }
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                                                            <option value="">(none)</option>
                                                            {Object.values(RiskTier).map((t) => (
                                                                <option key={t} value={t}>
                                                                    {formatRiskTierLabel(t)}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-priority-weight"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Priority Weight (1–100)
                                                        </label>
                                                        <input
                                                            type="number"
                                                            id="biomatch-priority-weight"
                                                            min={1}
                                                            max={100}
                                                            value={state.priority_weight ?? ''}
                                                            onChange={(e) =>
                                                                setState({
                                                                    ...state,
                                                                    priority_weight:
                                                                        e.target.value === ''
                                                                            ? null
                                                                            : Number(e.target.value),
                                                                })
                                                            }
                                                            placeholder="65"
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Sup type — only meaningful for supplements */}
                                                {recType === RecommendationType.SUPPLEMENT && (
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-sup-type"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Supplement Type{' '}
                                                            <span className="font-normal text-gray-400">
                                                                (optional)
                                                            </span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="biomatch-sup-type"
                                                            value={state.sup_type}
                                                            onChange={(e) =>
                                                                setState({...state, sup_type: e.target.value})
                                                            }
                                                            placeholder="e.g. vitamin, mineral"
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                )}

                                                {/* Pricing — hidden for care_pathway and referral */}
                                                {(isFieldVisible('price', recType) ||
                                                    isFieldVisible('members_price', recType)) && (
                                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                        {isFieldVisible('price', recType) && (
                                                            <div>
                                                                <label
                                                                    htmlFor="biomatch-price"
                                                                    className="mb-1 block text-xs font-medium text-gray-700">
                                                                    Price ($) <span className="text-mld-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    id="biomatch-price"
                                                                    value={state.price}
                                                                    onChange={(e) =>
                                                                        setState({
                                                                            ...state,
                                                                            price: parseFloat(e.target.value) || 0,
                                                                        })
                                                                    }
                                                                    min="0.01"
                                                                    step="0.01"
                                                                    required
                                                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                />
                                                            </div>
                                                        )}
                                                        {isFieldVisible('members_price', recType) && (
                                                            <div>
                                                                <label
                                                                    htmlFor="biomatch-members-price"
                                                                    className="mb-1 block text-xs font-medium text-gray-700">
                                                                    Members Price ($){' '}
                                                                    <span className="text-mld-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    id="biomatch-members-price"
                                                                    value={state.members_price}
                                                                    onChange={(e) =>
                                                                        setState({
                                                                            ...state,
                                                                            members_price:
                                                                                parseFloat(e.target.value) || 0,
                                                                        })
                                                                    }
                                                                    min="0.01"
                                                                    step="0.01"
                                                                    required
                                                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Kit SKU — programs only */}
                                                {isFieldVisible('kit_sku', recType) && (
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-kit-sku"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Kit SKU <span className="text-mld-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="biomatch-kit-sku"
                                                            value={state.kit_sku}
                                                            onChange={(e) =>
                                                                setState({...state, kit_sku: e.target.value})
                                                            }
                                                            placeholder="e.g. CARDIAC-KIT-90"
                                                            required
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                )}

                                                {/* Pathway URL — care_pathway only */}
                                                {isFieldVisible('pathway_url', recType) && (
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-pathway-url"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Pathway URL <span className="text-mld-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="url"
                                                            id="biomatch-pathway-url"
                                                            value={state.pathway_url}
                                                            onChange={(e) =>
                                                                setState({...state, pathway_url: e.target.value})
                                                            }
                                                            placeholder="https://..."
                                                            required
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                )}

                                                {/* CTA — always shown */}
                                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-cta-button-label"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            CTA Button Label <span className="text-mld-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="biomatch-cta-button-label"
                                                            value={state.cta_button_label}
                                                            onChange={(e) =>
                                                                setState({...state, cta_button_label: e.target.value})
                                                            }
                                                            maxLength={60}
                                                            placeholder="e.g. Enroll in Cardiac Care"
                                                            required
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label
                                                            htmlFor="biomatch-cta-body-text"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            CTA Body Text{' '}
                                                            <span className="font-normal text-gray-400">
                                                                (optional)
                                                            </span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="biomatch-cta-body-text"
                                                            value={state.cta_body_text}
                                                            onChange={(e) =>
                                                                setState({...state, cta_body_text: e.target.value})
                                                            }
                                                            maxLength={300}
                                                            placeholder="Short message above the CTA button"
                                                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Track enrollment — programs (and optional for lifestyle) */}
                                                {isFieldVisible('track_enrollment', recType) && (
                                                    <label className="flex items-center gap-2 pt-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={state.track_enrollment}
                                                            onChange={(e) =>
                                                                setState({...state, track_enrollment: e.target.checked})
                                                            }
                                                            className="h-4 w-4 rounded border-gray-300 text-mld-primary focus:ring-mld-primary"
                                                        />
                                                        <span className="text-xs text-gray-700">
                                                            Track enrollment (reports anonymized counts to the employer
                                                            dashboard)
                                                        </span>
                                                    </label>
                                                )}

                                                {/* Billing model — who pays for the kit */}
                                                <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <label
                                                            htmlFor="billing-type"
                                                            className="mb-1 block text-xs font-medium text-gray-700">
                                                            Billing
                                                        </label>
                                                        <select
                                                            id="billing-type"
                                                            value={state.billing_type}
                                                            onChange={(e) =>
                                                                setState({
                                                                    ...state,
                                                                    billing_type: e.target.value as BillingType,
                                                                    employer_coverage_percent:
                                                                        e.target.value === BillingType.EMPLOYER_PARTIAL
                                                                            ? (state.employer_coverage_percent ?? 80)
                                                                            : null,
                                                                })
                                                            }
                                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                                                            <option value={BillingType.EMPLOYER_FULL}>
                                                                Employer pays 100%
                                                            </option>
                                                            <option value={BillingType.EMPLOYER_PARTIAL}>
                                                                Employer / patient split
                                                            </option>
                                                            <option value={BillingType.PATIENT_FULL}>
                                                                Patient pays 100%
                                                            </option>
                                                        </select>
                                                    </div>

                                                    {state.billing_type === BillingType.EMPLOYER_PARTIAL && (
                                                        <div>
                                                            <label
                                                                htmlFor="employer-coverage"
                                                                className="mb-1 block text-xs font-medium text-gray-700">
                                                                Employer covers (%)
                                                            </label>
                                                            <input
                                                                id="employer-coverage"
                                                                type="number"
                                                                min={1}
                                                                max={99}
                                                                value={state.employer_coverage_percent ?? ''}
                                                                onChange={(e) => {
                                                                    const v = parseInt(e.target.value, 10);
                                                                    setState({
                                                                        ...state,
                                                                        employer_coverage_percent: Number.isFinite(v)
                                                                            ? Math.min(99, Math.max(1, v))
                                                                            : null,
                                                                    });
                                                                }}
                                                                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                placeholder="e.g., 80"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* Biomarker Rules — grouped */}
                                        <div className="pt-2">
                                            <div className="mb-2 flex items-baseline justify-between">
                                                <label className="block text-xs font-medium text-gray-700">
                                                    Biomarker Rules
                                                </label>
                                                <span className="text-[11px] text-gray-400">
                                                    AND within a group; OR across groups
                                                </span>
                                            </div>

                                            <div className="space-y-3">
                                                {groups.map((group, groupPosition) => (
                                                    <Fragment key={group.groupIndex}>
                                                        {groupPosition > 0 && (
                                                            <div className="flex items-center gap-3 py-1">
                                                                <div className="h-px flex-1 bg-gray-200" />
                                                                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                                                    OR
                                                                </span>
                                                                <div className="h-px flex-1 bg-gray-200" />
                                                            </div>
                                                        )}
                                                        <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/40 p-2">
                                                            {group.rules.map(({rule, flatIndex}) => (
                                                                <BiomarkerRuleRow
                                                                    key={flatIndex}
                                                                    rule={rule}
                                                                    index={flatIndex}
                                                                    removable={state.biomarkers.length > 1}
                                                                    onChange={(patch) => updateRule(flatIndex, patch)}
                                                                    onRemove={() => removeRule(flatIndex)}
                                                                    search={search}
                                                                />
                                                            ))}
                                                        </div>
                                                    </Fragment>
                                                ))}
                                            </div>

                                            <div className="mt-3 flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={addRuleToLastGroup}
                                                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-mld-primary/60 bg-mld-primary/5 px-3 py-2 text-xs font-medium text-mld-primary hover:bg-mld-primary/10">
                                                    <Plus className="h-3.5 w-3.5" />
                                                    Add another rule
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={addOrGroup}
                                                    className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">
                                                    <Plus className="h-3.5 w-3.5" />
                                                    Add OR group
                                                </button>
                                            </div>
                                        </div>

                                        {/* ── Recommended Assays + Products (BioMatch v2) ── */}
                                        <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-4">
                                            <div
                                                className={clsx(
                                                    'grid gap-6',
                                                    isRange ? 'md:grid-cols-2' : 'md:grid-cols-1'
                                                )}>
                                                {isRange && (
                                                    <RecommendedListPicker
                                                        title="Recommended Assays"
                                                        description="When this BioMatch fires, these assays roll into the patient's Custom Lab Package."
                                                        selectedKeys={selectedAssayKeys}
                                                        available={assayOptions}
                                                        onChange={setRecommendedAssayKeys}
                                                        placeholder={
                                                            assayCatalogQuery.isLoading
                                                                ? 'Loading catalog...'
                                                                : 'Add an assay...'
                                                        }
                                                        emptyText="No assays selected — this BioMatch won't add tests to the kit (fine for product-only recommendations)."
                                                    />
                                                )}
                                                <RecommendedListPicker
                                                    title="Recommended Products"
                                                    description={
                                                        isRange
                                                            ? 'Products surfaced alongside the Custom Lab Package when this BioMatch fires (supplements, protocols, etc.).'
                                                            : "Products that ride along with this curated program when it's recommended."
                                                    }
                                                    selectedKeys={selectedProductKeys}
                                                    available={productOptions}
                                                    onChange={setRecommendedProductKeys}
                                                    placeholder={
                                                        productsQuery.isLoading
                                                            ? 'Loading products...'
                                                            : 'Add a product...'
                                                    }
                                                    emptyText="No products linked. Optional — leave empty if no supplements/programs ride along."
                                                />
                                            </div>
                                        </div>

                                        <label className="flex items-center gap-2 pt-2">
                                            <input
                                                type="checkbox"
                                                checked={state.enabled}
                                                onChange={(e) => setState({...state, enabled: e.target.checked})}
                                                className="h-4 w-4 rounded border-gray-300 text-mld-primary focus:ring-mld-primary"
                                            />
                                            <span className="text-xs text-gray-700">Enabled</span>
                                        </label>
                                    </div>

                                    <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                            Cancel
                                        </button>
                                        <Button
                                            type="submit"
                                            loading={submitting}
                                            disabled={!isFormValid()}
                                            iconLeft={<Save className="h-4 w-4" />}>
                                            {submitting ? 'Saving...' : effectiveId ? 'Save Changes' : 'Create'}
                                        </Button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
